// AssetValuationDetailsModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Spinner,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Row,
    Col,
} from "reactstrap";
import { enqueueSnackbar } from 'notistack';
import PreliminaryDetailsTab from "./Tabs/PreliminaryDetailsTab";
import BuildingsTab from "./Tabs/BuildingsTab";
import EquipmentTab from "./Tabs/EquipmentTab";
import FurnitureTab from "./Tabs/FurnitureTab";
import ICTTab from "./Tabs/ICTTab";
import LandTab from "./Tabs/LandTab";
import MachineryTab from "./Tabs/MachineryTab";
import VehiclesTab from "./Tabs/VehiclesTab";
import SiteWorksTab from "./Tabs/SiteWorksTab";
import InfrastructureTab from "./Tabs/InfrastructureTab";
import {
    getAssetBuildings,
    getAssetsByValuation,
    getSiteWorks,
} from "../../../services/ValitanApiService";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../utils/exportUtils";
import {
    resolveAssetNameDisplay,
    resolveBuildingNameDisplay,
} from "../../../utils/otherValueDisplay";
import logo from "../../../assets/images/logo.png";
import Pagination from "../../../components/Pagination/Pagination";
import AssetImportModal from "./components/AssetImportModal";

const BRAND_BLUE = "#0147C8";
const DEFAULT_PAGE_SIZE = 10;

// Add spinner animation style
const spinnerStyle = `
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

const TAB_KEYS = [
    "buildings",
    "equipment",
    "furniture",
    "ict",
    "land",
    "machinery",
    "vehicles",
    "infrastructure",
    "siteworks",
];

const makeInitialPaginationByTab = () =>
    TAB_KEYS.reduce((acc, key) => {
        acc[key] = { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
        return acc;
    }, {});

const extractList = (response) => {
    const payload = response?.data?.data ?? response?.data ?? response ?? [];
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.content)) return payload.content;
    if (Array.isArray(payload?.data)) return payload.data;
    return [];
};

const AssetValuationDetailsModal = ({ isOpen, toggle, valuation }) => {
    const [activeTab, setActiveTab] = useState("preliminary");
    const [assets, setAssets] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [siteWorks, setSiteWorks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paginationByTab, setPaginationByTab] = useState(makeInitialPaginationByTab);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);
    const [columnModalOpen, setColumnModalOpen] = useState(false);
    const [pendingExportType, setPendingExportType] = useState(null);
    const [isExportingAll, setIsExportingAll] = useState(false);
    const [exportAllDropdownOpen, setExportAllDropdownOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);

    const IMPORTABLE_TABS = {
        furniture: "FURN",
        ict: "ICT",
        equipment: "EQP",
    };

    const reloadAssets = async () => {
        if (!valuation?.id) return;
        setLoading(true);
        try {
            const [assetsRes, buildingsRes, siteWorksRes] = await Promise.all([
                getAssetsByValuation(valuation.id),
                getAssetBuildings({
                    valuationId: valuation.id,
                    page: 0,
                    size: 500,
                }),
                getSiteWorks(0, 500, null, valuation.id),
            ]);

            setAssets(extractList(assetsRes));
            setBuildings(extractList(buildingsRes));
            setSiteWorks(extractList(siteWorksRes));
        } catch (err) {
            console.error("Failed to load assets:", err);
            setAssets([]);
            setBuildings([]);
            setSiteWorks([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen || !valuation?.id) return;
        reloadAssets();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, valuation?.id]);

    // When a new valuation is opened (or assets refresh), reset pagination to page 1 for all tabs.
    useEffect(() => {
        if (!isOpen) return;
        setPaginationByTab(makeInitialPaginationByTab());
    }, [isOpen, valuation?.id]);

    const filterByCategory = (code) => {
        return assets.filter((a) => a.asset_category?.code === code);
    };

    const buildingAssets = useMemo(
        () => assets.filter((asset) => asset.asset_category?.code === "BLDG"),
        [assets]
    );

    const getPaginatedAssets = (tabKey, categoryCode) => {
        const full = filterByCategory(categoryCode);
        const { pageNumber, pageSize } = paginationByTab?.[tabKey] || {
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        };
        const start = pageNumber * pageSize;
        return {
            full,
            paged: full.slice(start, start + pageSize),
            pageNumber,
            pageSize,
            totalElements: full.length,
            totalPages: Math.max(1, Math.ceil(full.length / pageSize)),
        };
    };

    const onPageChangeFor = (tabKey) => (pageNumber, pageSize) => {
        setPaginationByTab((prev) => ({
            ...prev,
            [tabKey]: { pageNumber, pageSize },
        }));
    };

    const onPageSizeChangeFor = (tabKey) => (pageSize) => {
        setPaginationByTab((prev) => ({
            ...prev,
            [tabKey]: { pageNumber: 0, pageSize },
        }));
    };

    const handlePreviousPageFor = (tabKey, totalPages) => () => {
        setPaginationByTab((prev) => {
            const current = prev?.[tabKey] || { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
            return {
                ...prev,
                [tabKey]: { ...current, pageNumber: Math.max(0, current.pageNumber - 1) },
            };
        });
    };

    const handleNextPageFor = (tabKey, totalPages) => () => {
        setPaginationByTab((prev) => {
            const current = prev?.[tabKey] || { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
            return {
                ...prev,
                [tabKey]: {
                    ...current,
                    pageNumber: Math.min(totalPages - 1, current.pageNumber + 1),
                },
            };
        });
    };

    // Get export headers based on category - same as AssetsByCategory
    const getExportHeaders = (category) => {
        const commonFields = [
            { key: "serialNumber", label: "#", defaultSelected: true },
            { key: "barcode", label: "Barcode", defaultSelected: true },
            { key: "name", label: "Name", defaultSelected: true },
            { key: "other_name", label: "Other Name", defaultSelected: false },
            { key: "description", label: "Description", defaultSelected: true },
            { key: "quantity", label: "Quantity", defaultSelected: true },
            { key: "serial_number", label: "Serial Number", defaultSelected: false },
            { key: "observed_condition_name", label: "Condition", defaultSelected: true },
            { key: "observed_condition", label: "Condition (Raw)", defaultSelected: false },
            { key: "remark", label: "Remark", defaultSelected: true },
            { key: "acquisition_date", label: "Acquisition Date", defaultSelected: false },
            { key: "useful_life_span_years", label: "Useful Life Span (Years)", defaultSelected: false },
        ];

        const locationFields = [
            { key: "ward", label: "Ward", defaultSelected: false },
            { key: "institution_name", label: "Institution Name", defaultSelected: false },
            { key: "institution_category", label: "Institution Category", defaultSelected: false },
            { key: "institution_category_other", label: "Institution Category Other", defaultSelected: false },
            { key: "institution_other", label: "Institution Other", defaultSelected: false },
            { key: "latitude", label: "Latitude", defaultSelected: false },
            { key: "longitude", label: "Longitude", defaultSelected: false },
        ];

        const buildingFields = [
            { key: "building_name", label: "Building Name", defaultSelected: false },
            { key: "other_building_name", label: "Other Building Name", defaultSelected: false },
            { key: "building_number", label: "Building Number", defaultSelected: false },
            { key: "floor", label: "Floor", defaultSelected: false },
            { key: "room_name_or_number", label: "Room Name/Number", defaultSelected: false },
            { key: "room_others", label: "Room Others", defaultSelected: false },
            { key: "number_of_buildings", label: "Number of Buildings", defaultSelected: false },
            { key: "building_use", label: "Building Use", defaultSelected: false },
        ];

        const landFields = [
            { key: "plot_number", label: "Plot Number", defaultSelected: false },
            { key: "plot_size", label: "Plot Size", defaultSelected: false },
            { key: "size_unit", label: "Size Unit", defaultSelected: false },
            { key: "block_number", label: "Block Number", defaultSelected: false },
            { key: "certificate_of_title", label: "Certificate of Title", defaultSelected: false },
            { key: "tenure_years", label: "Tenure Years", defaultSelected: false },
            { key: "rate_per_unit", label: "Rate Per Unit", defaultSelected: false },
            { key: "is_land_surveyed", label: "Is Land Surveyed", defaultSelected: false },
            { key: "registration_number", label: "Registration Number", defaultSelected: false },
            { key: "right_of_occupance_start", label: "Right of Occupancy Start", defaultSelected: false },
            { key: "right_of_occupance_end", label: "Right of Occupancy End", defaultSelected: false },
        ];

        const vehicleFields = [
            { key: "registration_number", label: "Registration Number", defaultSelected: false },
            { key: "chassis_number", label: "Chassis Number", defaultSelected: false },
            { key: "engine_number", label: "Engine Number", defaultSelected: false },
            { key: "make", label: "Make", defaultSelected: false },
            { key: "model", label: "Model", defaultSelected: false },
            { key: "year_of_manufacture", label: "Year of Manufacture", defaultSelected: false },
            { key: "engine_capacity", label: "Engine Capacity (cc)", defaultSelected: false },
            { key: "fuel_type", label: "Fuel Type", defaultSelected: false },
            { key: "imported_from", label: "Imported From", defaultSelected: false },
            { key: "imported_from_others", label: "Imported From Others", defaultSelected: false },
            { key: "body_type", label: "Body Type", defaultSelected: false },
            { key: "body_type_others", label: "Body Type Others", defaultSelected: false },
        ];

        const utilityFields = [
            { key: "has_water", label: "Has Water", defaultSelected: false },
            { key: "has_electricity", label: "Has Electricity", defaultSelected: false },
            { key: "utilities", label: "Utilities (Combined)", defaultSelected: false },
        ];

        const measurementFields = [
            { key: "measurement", label: "Measurement", defaultSelected: false },
            { key: "measurement_unit", label: "Measurement Unit", defaultSelected: false },
        ];

        const financialFields = [
            { key: "current_replacement_cost", label: "Current Replacement Cost", defaultSelected: false },
            { key: "historical_cost", label: "Historical Cost", defaultSelected: false },
            { key: "accumulated_depreciation_amount", label: "Accumulated Depreciation", defaultSelected: false },
            { key: "fair_value", label: "Fair Value", defaultSelected: false },
            { key: "net_book_value", label: "Net Book Value", defaultSelected: false },
            { key: "remaining_useful_life_years", label: "Remaining Useful Life (Years)", defaultSelected: false },
            { key: "cost_opening_balance", label: "Cost Opening Balance", defaultSelected: false },
            { key: "cost_additions", label: "Cost Additions", defaultSelected: false },
            { key: "cost_disposals", label: "Cost Disposals", defaultSelected: false },
            { key: "cost_closing_balance", label: "Cost Closing Balance", defaultSelected: false },
            { key: "depreciation_opening_balance", label: "Depreciation Opening Balance", defaultSelected: false },
            { key: "depreciation_charge", label: "Depreciation Charge", defaultSelected: false },
            { key: "depreciation_disposals", label: "Depreciation Disposals", defaultSelected: false },
            { key: "depreciation_closing_balance", label: "Depreciation Closing Balance", defaultSelected: false },
            { key: "monthly_depreciation", label: "Monthly Depreciation", defaultSelected: false },
        ];

        const valuationFields = [
            { key: "client_name", label: "Client Name", defaultSelected: false },
            { key: "project_name", label: "Project Name", defaultSelected: false },
            { key: "valuer_name", label: "Valuer", defaultSelected: false },
        ];

        const auditFields = [
            { key: "created_at", label: "Created At", defaultSelected: false },
            { key: "updated_at", label: "Updated At", defaultSelected: false },
        ];

        const categorySpecificHeaders = {
            buildings: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...landFields,
                ...utilityFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            furniture: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            equipment: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            ict: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            land: [
                ...commonFields,
                ...locationFields,
                ...landFields,
                ...utilityFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            machinery: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...vehicleFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            vehicles: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...vehicleFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            infrastructure: [
                ...commonFields,
                ...locationFields,
                { key: "street", label: "Street", defaultSelected: true },
                { key: "type_of_bridge", label: "Type of Bridge", defaultSelected: true },
                { key: "width_m", label: "Width (m)", defaultSelected: true },
                { key: "span_m", label: "Span (m)", defaultSelected: true },
                { key: "thickness_m", label: "Thickness (m)", defaultSelected: true },
                { key: "depth_m", label: "Depth (m)", defaultSelected: true },
                { key: "type_of_foundation", label: "Type of Foundation", defaultSelected: true },
                { key: "deck_type", label: "Deck Type", defaultSelected: true },
                { key: "handrail_type", label: "Handrail Type", defaultSelected: true },
                { key: "number_of_steps", label: "Number of Steps", defaultSelected: false },
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
            siteworks: [
                ...commonFields,
                ...locationFields,
                ...buildingFields,
                ...measurementFields,
                ...financialFields,
                ...valuationFields,
                ...auditFields,
            ],
        };

        return categorySpecificHeaders[category] || commonFields;
    };

    // Open column selector modal
    const openColumnSelector = (exportType) => {
        const categoryAssets = filterByCategory(getCategoryCode(activeTab));
        if (categoryAssets.length === 0) {
            enqueueSnackbar(
                `No ${activeTab} assets found to export for this valuation.`,
                {
                    variant: 'warning',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
            return;
        }
        setPendingExportType(exportType);
        setColumnModalOpen(true);
    };

    // Get category code from tab key
    const getCategoryCode = (tabKey) => {
        const codeMap = {
            buildings: "BLDG",
            equipment: "EQP",
            furniture: "FURN",
            ict: "ICT",
            land: "LAND",
            machinery: "MACH",
            vehicles: "VEH",
            infrastructure: "INFR",
            siteworks: "SITE",
        };
        return codeMap[tabKey] || "";
    };

    // Perform export with selected columns
    const performExport = () => {
        const categoryAssets = filterByCategory(getCategoryCode(activeTab));
        const allHeaders = getExportHeaders(activeTab);

        // Read selected columns from DOM checkboxes
        const checkboxNodes = document.querySelectorAll('input[name="export-column"]');
        const selectedKeys = [];
        checkboxNodes.forEach((node, index) => {
            if (node.checked) {
                const header = allHeaders[index];
                if (header) {
                    selectedKeys.push(header.key);
                }
            }
        });

        const headers = allHeaders.filter((h) => selectedKeys.includes(h.key));

        if (headers.length === 0) {
            enqueueSnackbar(
                'Please select at least one column to export.',
                {
                    variant: 'info',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
            return;
        }

        // Process assets data
        const processedAssets = categoryAssets.map((asset, index) => {
            const processed = { ...asset };
            
            processed.serialNumber = index + 1;
            
            if (selectedKeys.includes('utilities')) {
                const utilities = [];
                if (asset.has_water) utilities.push('Water');
                if (asset.has_electricity) utilities.push('Electricity');
                processed.utilities = utilities.length > 0 ? utilities.join(', ') : '-';
            }
            
            if (selectedKeys.includes('building_use')) {
                processed.building_use = asset.building_use?.name || asset.building_use || '-';
            }
            
            if (selectedKeys.includes('ward')) {
                processed.ward = asset.ward?.name || asset.ward || '-';
            }
            
            if (selectedKeys.includes('has_water')) {
                processed.has_water = asset.has_water ? 'Yes' : 'No';
            }
            if (selectedKeys.includes('has_electricity')) {
                processed.has_electricity = asset.has_electricity ? 'Yes' : 'No';
            }
            if (selectedKeys.includes('is_land_surveyed')) {
                processed.is_land_surveyed = asset.is_land_surveyed ? 'Yes' : 'No';
            }
            
            if (selectedKeys.includes('acquisition_date') && asset.acquisition_date) {
                try {
                    processed.acquisition_date = new Date(asset.acquisition_date).toLocaleDateString();
                } catch (e) {
                    processed.acquisition_date = asset.acquisition_date;
                }
            }
            if (selectedKeys.includes('right_of_occupance_start') && asset.right_of_occupance_start) {
                try {
                    processed.right_of_occupance_start = new Date(asset.right_of_occupance_start).toLocaleDateString();
                } catch (e) {
                    processed.right_of_occupance_start = asset.right_of_occupance_start;
                }
            }
            if (selectedKeys.includes('right_of_occupance_end') && asset.right_of_occupance_end) {
                try {
                    processed.right_of_occupance_end = new Date(asset.right_of_occupance_end).toLocaleDateString();
                } catch (e) {
                    processed.right_of_occupance_end = asset.right_of_occupance_end;
                }
            }
            if (selectedKeys.includes('created_at') && asset.created_at) {
                try {
                    processed.created_at = new Date(asset.created_at).toLocaleString();
                } catch (e) {
                    processed.created_at = asset.created_at;
                }
            }
            if (selectedKeys.includes('updated_at') && asset.updated_at) {
                try {
                    processed.updated_at = new Date(asset.updated_at).toLocaleString();
                } catch (e) {
                    processed.updated_at = asset.updated_at;
                }
            }
            
            if (selectedKeys.includes('plot_size') && asset.plot_size) {
                processed.plot_size = typeof asset.plot_size === 'number' 
                    ? asset.plot_size.toLocaleString() 
                    : asset.plot_size;
            }
            if (selectedKeys.includes('rate_per_unit') && asset.rate_per_unit) {
                processed.rate_per_unit = typeof asset.rate_per_unit === 'number' 
                    ? asset.rate_per_unit.toLocaleString() 
                    : asset.rate_per_unit;
            }
            if (selectedKeys.includes('latitude') && asset.latitude) {
                processed.latitude = typeof asset.latitude === 'number' 
                    ? asset.latitude.toString() 
                    : asset.latitude;
            }
            if (selectedKeys.includes('longitude') && asset.longitude) {
                processed.longitude = typeof asset.longitude === 'number' 
                    ? asset.longitude.toString() 
                    : asset.longitude;
            }
            
            return processed;
        });

        const filename = `${activeTab}_assets_${valuation?.valuation_ref || 'valuation'}_${new Date().toISOString().split("T")[0]}`;
        
        if (pendingExportType === 'csv') {
            exportToCSV(processedAssets, headers, `${filename}.csv`);
        } else if (pendingExportType === 'excel') {
            exportToExcel(processedAssets, headers, `${filename}.xlsx`);
        } else if (pendingExportType === 'pdf') {
            let exportedBy = "System User";
            try {
                const authUser = localStorage.getItem("authUser");
                if (authUser) {
                    const { user } = JSON.parse(authUser);
                    if (user) {
                        const firstName = user.first_name || "";
                        const lastName = user.last_name || "";
                        exportedBy = `${firstName} ${lastName}`.trim() || user.username || user.email || "System User";
                    }
                }
            } catch (error) {
                console.warn("Could not get user info:", error);
            }

            const title = `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Assets - ${valuation?.valuation_ref || 'Valuation'}`;
            const metadata = {
                logoPath: logo,
                exportedBy: exportedBy,
                clientName: valuation?.client?.name || null,
                projectName: valuation?.project?.name || null,
            };
            
            exportToPDF(processedAssets, headers, `${filename}.pdf`, title, metadata);
        }
        
        setColumnModalOpen(false);
    };

    // Select/Deselect all columns
    const toggleAllColumns = () => {
        const checkboxNodes = document.querySelectorAll('input[name="export-column"]');
        const allSelected = checkboxNodes.length > 0 && Array.from(checkboxNodes).every((node) => node.checked);
        Array.from(checkboxNodes).forEach((node) => {
            node.checked = !allSelected;
        });
    };

    // Export handlers
    const handleExportCSV = () => openColumnSelector('csv');
    const handleExportExcel = () => openColumnSelector('excel');
    const handleExportPDF = () => openColumnSelector('pdf');

    // Export ALL data for this valuation (Details + Site Works + All Assets)
    const handleExportAllData = async (exportType) => {
        if (!valuation?.id) {
            enqueueSnackbar('Valuation ID is required for export.', { variant: 'error' });
            return;
        }

        setIsExportingAll(true);
        
        try {
            enqueueSnackbar(
                `Fetching all data for valuation ${valuation.valuation_ref || valuation.id}...`,
                {
                    variant: 'info',
                    autoHideDuration: 3000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );

            // 1. Get all assets for this valuation
            const assetsRes = await getAssetsByValuation(valuation.id);
            const allAssets = assetsRes?.data?.data || assetsRes?.data || assetsRes || [];
            
            console.log('Fetched assets:', allAssets.length);

            // 2. Get all site works for this valuation
            let allSiteWorks = [];
            let siteWorkPage = 0;
            const siteWorkPageSize = 1000;
            let hasMoreSiteWorks = true;

            while (hasMoreSiteWorks) {
                try {
                    const siteWorksRes = await getSiteWorks(siteWorkPage, siteWorkPageSize, null, valuation.id);
                    const siteWorksData = siteWorksRes.data?.data || siteWorksRes.data || {};
                    const siteWorks = siteWorksData.content || siteWorksData.data || [];

                    if (!siteWorks || siteWorks.length === 0) {
                        hasMoreSiteWorks = false;
                        break;
                    }

                    allSiteWorks = [...allSiteWorks, ...siteWorks];

                    const totalPages = siteWorksData.totalPages || siteWorksData.total_pages || 1;
                    if (siteWorks.length < siteWorkPageSize) {
                        hasMoreSiteWorks = false;
                    } else {
                        hasMoreSiteWorks = siteWorkPage < totalPages - 1;
                    }

                    siteWorkPage++;
                } catch (err) {
                    console.error("Error fetching site works:", err);
                    hasMoreSiteWorks = false;
                }
            }
            
            console.log('Fetched site works:', allSiteWorks.length);

            // 3. Prepare all data for export
            const allData = [];

            // Extract all details from PreliminaryDetailsTab
            const project = valuation.project || {};
            const client = valuation.client || {};
            const valuer = valuation.valuer_user || {};
            const council = project.council || {};
            const district = council.district || {};
            const company = project.company || {};
            const category = project.project_category || {};

            // Add VALUATION DETAILS section
            allData.push({
                section: "VALUATION DETAILS",
                subsection: "Valuation Overview",
                field_name: "Reference",
                field_value: valuation.valuation_ref || "-",
            });
            allData.push({
                section: "VALUATION DETAILS",
                subsection: "Valuation Overview",
                field_name: "Status",
                field_value: valuation.valuation_status || valuation.status || "-",
            });
            allData.push({
                section: "VALUATION DETAILS",
                subsection: "Valuation Overview",
                field_name: "Date",
                field_value: valuation.valuation_date || "-",
            });
            allData.push({
                section: "VALUATION DETAILS",
                subsection: "Valuation Overview",
                field_name: "Currency",
                field_value: valuation.currency || "TZS",
            });
            if (valuation.notes) {
                allData.push({
                    section: "VALUATION DETAILS",
                    subsection: "Valuation Overview",
                    field_name: "Notes",
                    field_value: valuation.notes,
                });
            }

            // Add PROJECT DETAILS section
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "Project Name",
                field_value: project.name || "-",
            });
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "Project Code",
                field_value: project.code || "-",
            });
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "Category",
                field_value: category.name || "-",
            });
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "Council",
                field_value: council.name || "-",
            });
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "District",
                field_value: district.name || "-",
            });
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "Start Date",
                field_value: project.start_date || "-",
            });
            allData.push({
                section: "PROJECT DETAILS",
                subsection: "Project Information",
                field_name: "End Date",
                field_value: project.end_date || "-",
            });

            // Add CLIENT DETAILS section
            allData.push({
                section: "CLIENT DETAILS",
                subsection: "Client Information",
                field_name: "Client Name",
                field_value: client.name || "-",
            });
            allData.push({
                section: "CLIENT DETAILS",
                subsection: "Client Information",
                field_name: "TIN",
                field_value: client.tin || "-",
            });
            allData.push({
                section: "CLIENT DETAILS",
                subsection: "Client Information",
                field_name: "Type",
                field_value: client.is_government ? "Government" : "Private",
            });
            allData.push({
                section: "CLIENT DETAILS",
                subsection: "Client Information",
                field_name: "Email",
                field_value: client.email || "-",
            });
            allData.push({
                section: "CLIENT DETAILS",
                subsection: "Client Information",
                field_name: "Mobile",
                field_value: client.mobile_number || "-",
            });

            // Add CONTRACTOR DETAILS section (if available)
            if (company.name) {
                allData.push({
                    section: "CONTRACTOR DETAILS",
                    subsection: "Contractor Information",
                    field_name: "Company Name",
                    field_value: company.name || "-",
                });
                allData.push({
                    section: "CONTRACTOR DETAILS",
                    subsection: "Contractor Information",
                    field_name: "TIN",
                    field_value: company.tin || "-",
                });
                allData.push({
                    section: "CONTRACTOR DETAILS",
                    subsection: "Contractor Information",
                    field_name: "Email",
                    field_value: company.email || "-",
                });
                allData.push({
                    section: "CONTRACTOR DETAILS",
                    subsection: "Contractor Information",
                    field_name: "Mobile",
                    field_value: company.mobile_number || "-",
                });
            }

            // Add VALUER INFORMATION section
            const valuerFullName = [valuer.first_name, valuer.middle_name, valuer.last_name]
                .filter(Boolean)
                .join(" ") || "-";
            allData.push({
                section: "VALUER INFORMATION",
                subsection: "Valuer Details",
                field_name: "Name",
                field_value: valuerFullName,
            });
            allData.push({
                section: "VALUER INFORMATION",
                subsection: "Valuer Details",
                field_name: "Certificate Number",
                field_value: valuer.certificate_number || "-",
            });
            allData.push({
                section: "VALUER INFORMATION",
                subsection: "Valuer Details",
                field_name: "Email",
                field_value: valuer.email || "-",
            });
            allData.push({
                section: "VALUER INFORMATION",
                subsection: "Valuer Details",
                field_name: "Mobile",
                field_value: valuer.mobile_number || "-",
            });
            allData.push({
                section: "VALUER INFORMATION",
                subsection: "Valuer Details",
                field_name: "Sex",
                field_value: valuer.sex || "-",
            });
            allData.push({
                section: "VALUER INFORMATION",
                subsection: "Valuer Details",
                field_name: "Address",
                field_value: valuer.address || "-",
            });

            // Add SITE WORKS section
            if (allSiteWorks.length > 0) {
                allData.push({
                    section: "SITE WORKS",
                    subsection: "Summary",
                    field_name: "Total Count",
                    field_value: allSiteWorks.length.toString(),
                });
                
                allSiteWorks.forEach((sw, index) => {
                    const asset = sw.asset || {};
                    allData.push({
                        section: "SITE WORKS",
                        subsection: "Site Work Item",
                        row_number: index + 1,
                        site_work_type: sw.site_work_type || "",
                        other_specify: sw.other_specify || "",
                        description: sw.description || asset.description || "",
                        dimensions: sw.dimensions || "",
                        serial_number: sw.serial_number || asset.serial_number || "",
                        location: asset.location || "",
                        observed_condition: sw.observed_condition || asset.observed_condition || "",
                        remark: sw.remark || asset.remark || "",
                    });
                });
            } else {
                allData.push({
                    section: "SITE WORKS",
                    subsection: "Summary",
                    field_name: "Total Count",
                    field_value: "0",
                });
            }

            // Add all assets by category
            const categories = [
                { code: "BLDG", name: "BUILDINGS" },
                { code: "EQP", name: "EQUIPMENT" },
                { code: "FURN", name: "FURNITURE" },
                { code: "ICT", name: "ICT" },
                { code: "LAND", name: "LAND" },
                { code: "MACH", name: "MACHINERY" },
                { code: "VEH", name: "VEHICLES" },
                { code: "INFR", name: "INFRASTRUCTURE" },
            ];

            categories.forEach(category => {
                const categoryAssets = allAssets.filter(a => a.asset_category?.code === category.code);
                if (categoryAssets.length > 0) {
                    allData.push({
                        section: category.name,
                        subsection: "Summary",
                        field_name: "Total Count",
                        field_value: categoryAssets.length.toString(),
                    });

                    categoryAssets.forEach((asset, index) => {
                        const processed = { ...asset };
                        processed.section = category.name;
                        processed.subsection = "Asset Item";
                        processed.row_number = index + 1;
                        processed.serialNumber = index + 1;

                        // Format utilities
                        if (asset.has_water !== undefined && asset.has_electricity !== undefined) {
                            const water = asset.has_water ? "Water" : "";
                            const electricity = asset.has_electricity ? "Electricity" : "";
                            processed.utilities = [water, electricity].filter(Boolean).join(", ") || "None";
                        }

                        processed.name = resolveAssetNameDisplay(asset);
                        processed.building_name = resolveBuildingNameDisplay(asset, processed.building_name || "-");

                        allData.push(processed);
                    });
                } else {
                    allData.push({
                        section: category.name,
                        subsection: "Summary",
                        field_name: "Total Count",
                        field_value: "0",
                    });
                }
            });

            console.log('Total data rows to export:', allData.length);
            
            if (allData.length === 0) {
                enqueueSnackbar('No data found to export.', { variant: 'warning' });
                setIsExportingAll(false);
                return;
            }

            // Perform export
            const valuationRef = valuation.valuation_ref || valuation.valuation_number || valuation.id;
            const filename = `valuation_${valuationRef}_all_data_${new Date().toISOString().split('T')[0]}`;

            if (exportType === 'csv') {
                // Export as CSV with sections
                exportAllDataToCSV(allData, filename);
            } else if (exportType === 'excel') {
                // Export as Excel with multiple sheets
                exportAllDataToExcel(allData, filename, valuation);
            } else if (exportType === 'pdf') {
                let exportedBy = "System User";
                try {
                    const authUser = localStorage.getItem("authUser");
                    if (authUser) {
                        const user = JSON.parse(authUser);
                        if (user) {
                            const firstName = user.first_name || user.firstName || "";
                            const lastName = user.last_name || user.lastName || "";
                            exportedBy = `${firstName} ${lastName}`.trim() || user.username || user.email || "System User";
                        }
                    }
                } catch (error) {
                    console.error("Error getting user info:", error);
                }

                // Export as PDF with sections
                exportAllDataToPDF(allData, filename, valuation, exportedBy);
            }

            enqueueSnackbar(
                `Successfully exported all data (${allAssets.length} assets, ${allSiteWorks.length} site works).`,
                {
                    variant: 'success',
                    autoHideDuration: 4000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        } catch (error) {
            console.error("Error exporting all data:", error);
            enqueueSnackbar(
                'Failed to export all data. Please try again.',
                {
                    variant: 'error',
                    autoHideDuration: 5000,
                    anchorOrigin: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }
            );
        } finally {
            setIsExportingAll(false);
        }
    };

    // Export all data to CSV with sections
    const exportAllDataToCSV = (allData, filename) => {
        const sections = {};
        
        // Group data by section
        allData.forEach(row => {
            const section = row.section || "OTHER";
            if (!sections[section]) {
                sections[section] = [];
            }
            sections[section].push(row);
        });

        // Create CSV content with sections
        let csvContent = "";
        
        // Order sections
        const sectionOrder = [
            "VALUATION DETAILS",
            "PROJECT DETAILS",
            "CLIENT DETAILS",
            "CONTRACTOR DETAILS",
            "VALUER INFORMATION",
            "SITE WORKS",
            "BUILDINGS",
            "EQUIPMENT",
            "FURNITURE",
            "ICT",
            "LAND",
            "MACHINERY",
            "VEHICLES",
            "INFRASTRUCTURE",
        ];

        sectionOrder.forEach(sectionName => {
            if (sections[sectionName] && sections[sectionName].length > 0) {
                // Add section header
                csvContent += `\n\n=== ${sectionName} ===\n`;
                
                const sectionData = sections[sectionName];
                
                // Determine headers based on section type
                let headers = [];
                if (sectionName === "VALUATION DETAILS" || sectionName === "PROJECT DETAILS" || 
                    sectionName === "CLIENT DETAILS" || sectionName === "CONTRACTOR DETAILS" || 
                    sectionName === "VALUER INFORMATION") {
                    // Details sections - use field_name and field_value
                    headers = [
                        { key: "field_name", label: "Field Name" },
                        { key: "field_value", label: "Value" }
                    ];
                } else {
                    // Asset sections - use asset fields
                    const firstRow = sectionData.find(r => r.subsection === "Asset Item" || r.subsection === "Site Work Item");
                    if (firstRow) {
                        // Get all keys from first asset row
                        const keys = Object.keys(firstRow).filter(k => 
                            k !== "section" && k !== "subsection" && 
                            k !== "field_name" && k !== "field_value"
                        );
                        headers = keys.map(key => ({
                            key: key,
                            label: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                        }));
                    }
                }
                
                if (headers.length > 0) {
                    // Add header row
                    csvContent += headers.map(h => `"${h.label}"`).join(",") + "\n";
                    
                    // Add data rows
                    sectionData.forEach(row => {
                        if (row.subsection === "Asset Item" || row.subsection === "Site Work Item" || 
                            row.subsection === "Valuation Overview" || row.subsection === "Project Information" ||
                            row.subsection === "Client Information" || row.subsection === "Contractor Information" ||
                            row.subsection === "Valuer Details") {
                            const rowData = headers.map(h => {
                                let value = row[h.key];
                                if (value === null || value === undefined) value = "";
                                value = String(value).replace(/"/g, '""');
                                return `"${value}"`;
                            });
                            csvContent += rowData.join(",") + "\n";
                        }
                    });
                }
            }
        });

        // Add BOM for UTF-8
        const BOM = "\uFEFF";
        const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `${filename}.csv`);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Export all data to Excel with multiple sheets
    const exportAllDataToExcel = (allData, filename, valuation) => {
        import("xlsx").then((XLSX) => {
            const wb = XLSX.utils.book_new();
            
            // Group data by section
            const sections = {};
            allData.forEach(row => {
                const section = row.section || "OTHER";
                if (!sections[section]) {
                    sections[section] = [];
                }
                sections[section].push(row);
            });

            // Order sections
            const sectionOrder = [
                "VALUATION DETAILS",
                "PROJECT DETAILS",
                "CLIENT DETAILS",
                "CONTRACTOR DETAILS",
                "VALUER INFORMATION",
                "SITE WORKS",
                "BUILDINGS",
                "EQUIPMENT",
                "FURNITURE",
                "ICT",
                "LAND",
                "MACHINERY",
                "VEHICLES",
                "INFRASTRUCTURE",
            ];

            sectionOrder.forEach(sectionName => {
                if (sections[sectionName] && sections[sectionName].length > 0) {
                    const sectionData = sections[sectionName];
                    
                    // Determine headers based on section type
                    let headers = [];
                    let worksheetData = [];
                    
                    if (sectionName === "VALUATION DETAILS" || sectionName === "PROJECT DETAILS" || 
                        sectionName === "CLIENT DETAILS" || sectionName === "CONTRACTOR DETAILS" || 
                        sectionName === "VALUER INFORMATION") {
                        // Details sections - use field_name and field_value
                        headers = [
                            { key: "field_name", label: "Field Name" },
                            { key: "field_value", label: "Value" }
                        ];
                        worksheetData = [
                            headers.map(h => h.label),
                            ...sectionData
                                .filter(r => r.subsection !== "Summary")
                                .map(row => [
                                    row.field_name || "",
                                    row.field_value || ""
                                ])
                        ];
                    } else {
                        // Asset sections - use asset fields
                        const assetRows = sectionData.filter(r => 
                            r.subsection === "Asset Item" || r.subsection === "Site Work Item"
                        );
                        
                        if (assetRows.length > 0) {
                            // Get all keys from first asset row
                            const firstRow = assetRows[0];
                            const keys = Object.keys(firstRow).filter(k => 
                                k !== "section" && k !== "subsection" && 
                                k !== "field_name" && k !== "field_value"
                            );
                            headers = keys.map(key => ({
                                key: key,
                                label: key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                            }));
                            
                            worksheetData = [
                                headers.map(h => h.label),
                                ...assetRows.map(row =>
                                    headers.map(h => {
                                        let value = row[h.key];
                                        if (value === null || value === undefined) value = "";
                                        if (typeof value === "object") {
                                            value = value.name || value.code || "";
                                        }
                                        return value;
                                    })
                                )
                            ];
                        }
                    }
                    
                    if (worksheetData.length > 0) {
                        const ws = XLSX.utils.aoa_to_sheet(worksheetData);
                        
                        // Auto-size columns
                        const colWidths = headers.map((h, idx) => {
                            const maxLength = Math.max(
                                h.label.length,
                                ...worksheetData.slice(1).map(row => String(row[idx] || "").length)
                            );
                            return { wch: Math.min(Math.max(maxLength + 2, 10), 50) };
                        });
                        ws["!cols"] = colWidths;
                        
                        // Add sheet to workbook (limit sheet name to 31 chars)
                        const sheetName = sectionName.length > 31 ? sectionName.substring(0, 31) : sectionName;
                        XLSX.utils.book_append_sheet(wb, ws, sheetName);
                    }
                }
            });

            // Write file
            XLSX.writeFile(wb, `${filename}.xlsx`);
        });
    };

    // Export all data to PDF with sections
    const exportAllDataToPDF = (allData, filename, valuation, exportedBy) => {
        import("jspdf").then((jsPDFModule) => {
            const jsPDF = jsPDFModule.default;
            
            return import("jspdf-autotable").then(() => {
                const doc = new jsPDF({
                    orientation: "landscape",
                    unit: "mm",
                    format: "a4"
                });

                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();
                let currentY = 15;

                // Add logo
                try {
                    doc.addImage(logo, "PNG", 14, 10, 25, 15);
                    currentY = 30;
                } catch (error) {
                    console.warn("Could not add logo:", error);
                    currentY = 15;
                }

                // Add company name and title
                doc.setFontSize(18);
                doc.setFont(undefined, "bold");
                doc.setTextColor(1, 71, 200);
                doc.text("TD PROPERTY DEVELOPERS", pageWidth - 14, 15, { align: "right" });
                
                const valuationRef = valuation.valuation_ref || valuation.valuation_number || valuation.id;
                const title = `Valuation ${valuationRef} - Complete Data Report`;
                doc.setFontSize(14);
                doc.setTextColor(0, 0, 0);
                doc.text(title, pageWidth - 14, 22, { align: "right" });

                currentY = Math.max(currentY, 30);

                // Add Client and Project names
                const clientName = valuation.client?.name || valuation.client_name || "";
                const projectName = valuation.project?.name || valuation.project_name || "";
                
                if (clientName || projectName) {
                    doc.setFontSize(11);
                    doc.setFont(undefined, "bold");
                    doc.setTextColor(0, 0, 0);
                    
                    if (clientName) {
                        doc.text(`Client: ${clientName}`, 14, currentY);
                        currentY += 6;
                    }
                    
                    if (projectName) {
                        doc.text(`Project: ${projectName}`, 14, currentY);
                        currentY += 6;
                    }
                    
                    currentY += 2;
                }

                // Add export date and time
                doc.setFontSize(9);
                doc.setFont(undefined, "normal");
                doc.setTextColor(80, 80, 80);
                const exportDateTime = new Date().toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                });
                doc.setFont(undefined, "bold");
                doc.text("Exported:", 14, currentY);
                doc.setFont(undefined, "normal");
                doc.text(exportDateTime, 35, currentY);
                currentY += 8;

                // Group data by section
                const sections = {};
                allData.forEach(row => {
                    const section = row.section || "OTHER";
                    if (!sections[section]) {
                        sections[section] = [];
                    }
                    sections[section].push(row);
                });

                // Order sections
                const sectionOrder = [
                    "VALUATION DETAILS",
                    "PROJECT DETAILS",
                    "CLIENT DETAILS",
                    "CONTRACTOR DETAILS",
                    "VALUER INFORMATION",
                    "SITE WORKS",
                    "BUILDINGS",
                    "EQUIPMENT",
                    "FURNITURE",
                    "ICT",
                    "LAND",
                    "MACHINERY",
                    "VEHICLES",
                    "INFRASTRUCTURE",
                ];

                sectionOrder.forEach(sectionName => {
                    if (sections[sectionName] && sections[sectionName].length > 0) {
                        const sectionData = sections[sectionName];
                        
                        // Check if we need a new page
                        if (currentY > pageHeight - 40) {
                            doc.addPage();
                            currentY = 15;
                        }
                        
                        // Add section title
                        doc.setFontSize(12);
                        doc.setFont(undefined, "bold");
                        doc.setTextColor(1, 71, 200);
                        doc.text(sectionName, 14, currentY);
                        currentY += 8;
                        
                        // Determine headers and data based on section type
                        let headers = [];
                        let tableData = [];
                        
                        if (sectionName === "VALUATION DETAILS" || sectionName === "PROJECT DETAILS" || 
                            sectionName === "CLIENT DETAILS" || sectionName === "CONTRACTOR DETAILS" || 
                            sectionName === "VALUER INFORMATION") {
                            // Details sections
                            headers = ["Field Name", "Value"];
                            tableData = sectionData
                                .filter(r => r.subsection !== "Summary")
                                .map(row => [
                                    row.field_name || "",
                                    row.field_value || ""
                                ]);
                        } else {
                            // Asset sections
                            const assetRows = sectionData.filter(r => 
                                r.subsection === "Asset Item" || r.subsection === "Site Work Item"
                            );
                            
                            if (assetRows.length > 0) {
                                const firstRow = assetRows[0];
                                const keys = Object.keys(firstRow).filter(k => 
                                    k !== "section" && k !== "subsection" && 
                                    k !== "field_name" && k !== "field_value" &&
                                    (k === "row_number" || k === "serialNumber" || k === "name" || 
                                     k === "building_name" || k === "description" || k === "category_name" ||
                                     k === "location" || k === "serial_number" || k === "site_work_type" ||
                                     k === "other_specify" || k === "dimensions" || k === "observed_condition" ||
                                     k === "remark" || k === "current_replacement_cost" || k === "fair_value" ||
                                     k === "net_book_value" || k === "utilities")
                                );
                                headers = keys.map(key => 
                                    key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                                );
                                
                                tableData = assetRows.map(row =>
                                    keys.map(key => {
                                        let value = row[key];
                                        if (value === null || value === undefined) value = "-";
                                        if (typeof value === "object") {
                                            value = value.name || value.code || "";
                                        }
                                        return String(value);
                                    })
                                );
                            }
                        }
                        
                        if (headers.length > 0 && tableData.length > 0) {
                            doc.autoTable({
                                head: [headers],
                                body: tableData,
                                startY: currentY,
                                styles: {
                                    fontSize: 7,
                                    cellPadding: 1.5,
                                    overflow: "linebreak",
                                    cellWidth: "wrap"
                                },
                                headStyles: {
                                    fillColor: [1, 71, 200],
                                    textColor: 255,
                                    fontStyle: "bold",
                                    halign: "center",
                                    fontSize: 8
                                },
                                alternateRowStyles: {
                                    fillColor: [245, 245, 245]
                                },
                                margin: { top: currentY, left: 10, right: 10, bottom: 20 },
                                didDrawPage: (data) => {
                                    const footerY = pageHeight - 12;
                                    doc.setDrawColor(200, 200, 200);
                                    doc.line(14, footerY - 3, pageWidth - 14, footerY - 3);
                                    doc.setFontSize(8);
                                    doc.setTextColor(100, 100, 100);
                                    doc.setFont(undefined, "normal");
                                    if (exportedBy) {
                                        doc.text(`Exported by: ${exportedBy}`, 14, footerY);
                                    }
                                    const pageCount = doc.internal.getNumberOfPages();
                                    doc.text(
                                        `Page ${data.pageNumber} of ${pageCount}`,
                                        pageWidth / 2,
                                        footerY,
                                        { align: "center" }
                                    );
                                    doc.text("TD Property Developers", pageWidth - 14, footerY, { align: "right" });
                                }
                            });
                            
                            // Get final Y position after table
                            const finalY = doc.lastAutoTable.finalY || currentY;
                            currentY = finalY + 10;
                        }
                    }
                });

                // Save the PDF
                doc.save(`${filename}.pdf`);
            });
        }).catch(error => {
            console.error("Error loading PDF libraries:", error);
            alert("Failed to generate PDF. Please make sure jsPDF library is installed.");
        });
    };

    const renderCategoryTab = (tabKey, categoryCode, TabComponent) => {
        const { paged, pageNumber, pageSize, totalElements, totalPages } = getPaginatedAssets(
            tabKey,
            categoryCode
        );

        return (
            <>
                <TabComponent assets={paged} />
                {totalElements > 0 && (
                    <div className="mt-3">
                        <Pagination
                            paginationInfo={{ pageNumber, pageSize, totalPages, totalElements }}
                            handleNextPage={handleNextPageFor(tabKey, totalPages)}
                            handlePreviousPage={handlePreviousPageFor(tabKey, totalPages)}
                            onPageChange={onPageChangeFor(tabKey)}
                            onPageSizeChange={onPageSizeChangeFor(tabKey)}
                        />
                    </div>
                )}
            </>
        );
    };

    const renderDataTab = (tabKey, items, TabComponent, extraProps = {}) => {
        const { pageNumber, pageSize } = paginationByTab?.[tabKey] || {
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
        };
        const start = pageNumber * pageSize;
        const paged = items.slice(start, start + pageSize);
        const totalElements = items.length;
        const totalPages = Math.max(1, Math.ceil(totalElements / pageSize));

        return (
            <>
                <TabComponent assets={paged} {...extraProps} />
                {totalElements > 0 && (
                    <div className="mt-3">
                        <Pagination
                            paginationInfo={{ pageNumber, pageSize, totalPages, totalElements }}
                            handleNextPage={handleNextPageFor(tabKey, totalPages)}
                            handlePreviousPage={handlePreviousPageFor(tabKey, totalPages)}
                            onPageChange={onPageChangeFor(tabKey)}
                            onPageSizeChange={onPageSizeChangeFor(tabKey)}
                        />
                    </div>
                )}
            </>
        );
    };

    // Clean, minimal tab style
    const tabStyle = (isActive) => ({
        padding: "10px 16px",
        margin: "0 6px",
        borderRadius: "50px",
        fontWeight: "600",
        fontSize: "13px",
        color: isActive ? "white" : "#333",
        backgroundColor: isActive ? BRAND_BLUE : "transparent",
        border: "none",
        cursor: "pointer",
        transition: "all 0.25s ease",
        whiteSpace: "nowrap",
    });

    return (
        <>
            <style>{spinnerStyle}</style>
            <Modal isOpen={isOpen} toggle={toggle} size="xl" backdrop="static" keyboard={false}>
                <ModalHeader toggle={toggle} className="border-0 pb-0">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <h5 className="mb-0 fw-bold" style={{ color: BRAND_BLUE }}>
                            Valuation — {valuation?.valuation_ref || "N/A"}
                        </h5>
                        <Dropdown
                            isOpen={exportAllDropdownOpen}
                            toggle={() => {
                                if (!isExportingAll) {
                                    setExportAllDropdownOpen(!exportAllDropdownOpen);
                                }
                            }}
                        >
                            <DropdownToggle
                                caret
                                size="sm"
                                style={{
                                    backgroundColor: '#28a745',
                                    borderColor: '#28a745',
                                    color: "white",
                                }}
                                disabled={isExportingAll}
                            >
                                {isExportingAll ? (
                                    <i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
                                ) : (
                                    <>
                                        <i className="ri-download-line me-2"></i>
                                        Export All Data
                                    </>
                                )}
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={() => {
                                    setExportAllDropdownOpen(false);
                                    handleExportAllData('csv');
                                }}>
                                    <i className="ri-file-text-line me-2"></i>
                                    Export All as CSV
                                </DropdownItem>
                                <DropdownItem onClick={() => {
                                    setExportAllDropdownOpen(false);
                                    handleExportAllData('excel');
                                }}>
                                    <i className="ri-file-excel-line me-2"></i>
                                    Export All as Excel
                                </DropdownItem>
                                <DropdownItem onClick={() => {
                                    setExportAllDropdownOpen(false);
                                    handleExportAllData('pdf');
                                }}>
                                    <i className="ri-file-pdf-line me-2"></i>
                                    Export All as PDF
                                </DropdownItem>
                            </DropdownMenu>
                        </Dropdown>
                    </div>
                </ModalHeader>

            <ModalBody className="pt-3 px-4">
                {/* Actions above tabs: import (FURN/ICT) + export */}
                {activeTab !== "preliminary" && (
                    <div className="d-flex justify-content-end align-items-center gap-2 mb-3 flex-wrap">
                        {IMPORTABLE_TABS[activeTab] && (
                            <Button
                                size="sm"
                                color="success"
                                outline
                                onClick={() => setImportModalOpen(true)}
                                title={`Import ${IMPORTABLE_TABS[activeTab]} assets from Excel`}
                            >
                                <i className="ri-upload-2-line me-1"></i>
                                Import Excel
                            </Button>
                        )}
                        {((activeTab === "buildings" && buildings.length > 0) ||
                            (activeTab === "siteworks" && siteWorks.length > 0) ||
                            (activeTab !== "buildings" &&
                                activeTab !== "siteworks" &&
                                assets.length > 0)) && (
                            <Dropdown
                                isOpen={exportDropdownOpen}
                                toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                            >
                                <DropdownToggle
                                    caret
                                    size="sm"
                                    style={{
                                        backgroundColor: BRAND_BLUE,
                                        borderColor: BRAND_BLUE,
                                        color: "white",
                                    }}
                                >
                                    <i className="ri-download-line me-2"></i>
                                    Export {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                                </DropdownToggle>
                                <DropdownMenu>
                                    <DropdownItem onClick={handleExportCSV}>
                                        <i className="ri-file-text-line me-2"></i>
                                        Export as CSV
                                    </DropdownItem>
                                    <DropdownItem onClick={handleExportExcel}>
                                        <i className="ri-file-excel-line me-2"></i>
                                        Export as Excel
                                    </DropdownItem>
                                    <DropdownItem onClick={handleExportPDF}>
                                        <i className="ri-file-pdf-line me-2"></i>
                                        Export as PDF
                                    </DropdownItem>
                                </DropdownMenu>
                            </Dropdown>
                        )}
                    </div>
                )}

                {/* TABS — Clean & Minimal */}
                <Nav tabs className="flex-wrap justify-content-start gap-2 mb-4 border-bottom pb-3">
                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "preliminary")}
                            onClick={() => setActiveTab("preliminary")}
                        >
                            Details
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "buildings")}
                            onClick={() => setActiveTab("buildings")}
                        >
                            Buildings
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "equipment")}
                            onClick={() => setActiveTab("equipment")}
                        >
                            Equipment
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "furniture")}
                            onClick={() => setActiveTab("furniture")}
                        >
                            Furniture
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "ict")}
                            onClick={() => setActiveTab("ict")}
                        >
                            ICT
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "land")}
                            onClick={() => setActiveTab("land")}
                        >
                            Land
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "machinery")}
                            onClick={() => setActiveTab("machinery")}
                        >
                            Machinery
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "vehicles")}
                            onClick={() => setActiveTab("vehicles")}
                        >
                            Vehicles
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "infrastructure")}
                            onClick={() => setActiveTab("infrastructure")}
                        >
                            Infrastructure
                        </NavLink>
                    </NavItem>

                    <NavItem>
                        <NavLink
                            style={tabStyle(activeTab === "siteworks")}
                            onClick={() => setActiveTab("siteworks")}
                        >
                            Site Works
                        </NavLink>
                    </NavItem>
                </Nav>

                {/* TAB CONTENT */}
                <TabContent activeTab={activeTab}>
                    <TabPane tabId="preliminary">
                        <PreliminaryDetailsTab valuation={valuation} />
                    </TabPane>

                    {loading ? (
                        <TabPane tabId="loading">
                            <div className="text-center py-5">
                                <Spinner style={{ width: "3.5rem", height: "3.5rem", color: BRAND_BLUE }} />
                                <p className="mt-3 text-muted">Loading assets...</p>
                            </div>
                        </TabPane>
                    ) : (
                        <>
                            <TabPane tabId="buildings">
                                {renderDataTab("buildings", buildings, BuildingsTab, {
                                    sourceAssets: buildingAssets,
                                })}
                            </TabPane>
                            <TabPane tabId="equipment">
                                {renderCategoryTab("equipment", "EQP", EquipmentTab)}
                            </TabPane>
                            <TabPane tabId="furniture">
                                {renderCategoryTab("furniture", "FURN", FurnitureTab)}
                            </TabPane>
                            <TabPane tabId="ict">
                                {renderCategoryTab("ict", "ICT", ICTTab)}
                            </TabPane>
                            <TabPane tabId="land">
                                {renderCategoryTab("land", "LAND", LandTab)}
                            </TabPane>
                            <TabPane tabId="machinery">
                                {renderCategoryTab("machinery", "MACH", MachineryTab)}
                            </TabPane>
                            <TabPane tabId="vehicles">
                                {renderCategoryTab("vehicles", "VEH", VehiclesTab)}
                            </TabPane>
                            <TabPane tabId="infrastructure">
                                {renderCategoryTab("infrastructure", "INFR", InfrastructureTab)}
                            </TabPane>
                            <TabPane tabId="siteworks">
                                {renderDataTab("siteworks", siteWorks, SiteWorksTab)}
                            </TabPane>
                        </>
                    )}
                </TabContent>
            </ModalBody>

            <ModalFooter className="border-0 justify-content-center">
                <Button
                    onClick={toggle}
                    style={{
                        backgroundColor: BRAND_BLUE,
                        border: "none",
                        borderRadius: "50px",
                        padding: "12px 40px",
                        fontWeight: "600",
                        fontSize: "15px",
                    }}
                >
                    Close
                </Button>
            </ModalFooter>
            </Modal>

            <AssetImportModal
                isOpen={importModalOpen}
                toggle={() => setImportModalOpen(false)}
                assetValuationId={valuation?.id}
                valuationLabel={valuation?.valuation_ref || valuation?.valuationRef || valuation?.id}
                initialCategoryCode={IMPORTABLE_TABS[activeTab] || "FURN"}
                lockCategory={Boolean(IMPORTABLE_TABS[activeTab])}
                onImported={async () => {
                    setImportModalOpen(false);
                    await reloadAssets();
                }}
            />

            {/* Column Selector Modal */}
            <Modal 
                isOpen={columnModalOpen} 
                toggle={() => setColumnModalOpen(!columnModalOpen)}
                size="lg"
            >
                <ModalHeader toggle={() => setColumnModalOpen(!columnModalOpen)}>
                    <i className="ri-list-check me-2" style={{ color: BRAND_BLUE }}></i>
                    Select Columns to Export - {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
                </ModalHeader>
                <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <div className="mb-3 d-flex align-items-center justify-content-between">
                        <Button
                            color="primary"
                            size="sm"
                            outline
                            onClick={toggleAllColumns}
                            style={{ borderColor: BRAND_BLUE, color: BRAND_BLUE }}
                        >
                            <i className="ri-checkbox-multiple-line me-1"></i>
                            Select / Deselect All
                        </Button>
                        <small className="text-muted">
                            {getExportHeaders(activeTab).length} columns available
                        </small>
                    </div>

                    <Row>
                        {getExportHeaders(activeTab).map((header, index) => {
                            const checkboxId = `column-${header.key}-${index}`;
                            const isDefaultSelected =
                                header.defaultSelected === undefined
                                    ? true
                                    : header.defaultSelected;
                            return (
                                <Col md={6} lg={4} key={checkboxId} className="mb-2">
                                    <div className="form-check">
                                        <input
                                            type="checkbox"
                                            id={checkboxId}
                                            className="form-check-input"
                                            name="export-column"
                                            defaultChecked={isDefaultSelected}
                                            style={{ cursor: "pointer" }}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor={checkboxId}
                                            style={{ cursor: "pointer", userSelect: "none" }}
                                        >
                                            {header.label}
                                        </label>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="secondary" 
                        onClick={() => setColumnModalOpen(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={performExport}
                        style={{ 
                            backgroundColor: BRAND_BLUE, 
                            borderColor: BRAND_BLUE,
                        }}
                    >
                        <i className="ri-download-line me-2"></i>
                        Export {pendingExportType?.toUpperCase()}
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default AssetValuationDetailsModal;
